import type { PointerEvent, ReactNode } from 'react';
import type { Face, FinishId, Inscription, StampId, TrickId } from '../../engine';
import { t } from '../../i18n';
import { itemName } from '../../app/describe';
import { Glyph, WildEmblem } from './Glyph';
import { RouteGlyph } from './RouteGlyph';
import { SpritePaths } from './night/Sprite';
import { ORB_CROWNED } from './pixelPieces';

export type CardSize = 'xs' | 'sm' | 'md' | 'lg';
export type CardState = 'neutral' | 'playable' | 'unplayable' | 'selected';

interface CardViewProps {
  /** Printed face, or the named face of a Wild on the pile. */
  face?: Face | null;
  wild?: boolean;
  /** Shows the card as an Epitaph grave marker with its route. */
  epitaph?: Inscription | null;
  /** A face chalked on the pile (Long Night's Chalk Curio). */
  chalk?: boolean;
  /** Long Night: the card's Stamp and Finish. */
  stamp?: StampId;
  finish?: FinishId;
  /** Long Night: a Trick, named on the card. */
  trick?: TrickId;
  /** Long Night: a growing Stamp's value (the Tally Stamp's marks, the Glass Stamp's cracks). */
  stampValue?: number;
  /** Long Night: a card of the color a Tarnish rule dulls. */
  tarnished?: boolean;
  /** Long Night: an IOU, a curse that can't be played. */
  iou?: boolean;
  back?: boolean;
  size?: CardSize;
  state?: CardState;
  label: string;
  hint?: string;
  flipRoute?: boolean;
  onClick?: () => void;
  onFocus?: () => void;
  onBlur?: () => void;
  onPointerEnter?: () => void;
  onPointerLeave?: (e: PointerEvent<HTMLElement>) => void;
  onPointerMove?: (e: PointerEvent<HTMLElement>) => void;
  children?: ReactNode;
  pressed?: boolean;
  disabled?: boolean;
  id?: string;
}

/** A corner index: the number over its glyph, printed twice like on a playing card. */
/** The Wild's W in whole pixels, 9 × 7: the face's own letter reads as another letter at this size. */
const PIXEL_W = 'M0 0h2v4h1v-2h1v-1h1v1h1v2h1v-4h2v6h-1v1h-2v-1h-1v-1h-1v1h-1v1h-2v-1h-1z';

function PixelW() {
  return (
    <svg className="card-w" viewBox="0 0 9 7" shapeRendering="crispEdges" aria-hidden="true">
      <path d={PIXEL_W} fill="currentColor" />
    </svg>
  );
}

function Index({ face, text, corner }: { face?: Face; text?: string; corner: 'tl' | 'br' }) {
  return (
    <span className={`card-index card-index-${corner}`} aria-hidden="true">
      <span className="card-num">{face ? face.number : text === 'W' ? <PixelW /> : text}</span>
      {face && <Glyph color={face.color} size={12} />}
    </span>
  );
}

/** The face of a card: its color, with its glyph and number (§19.13), never color alone. */
function FaceArt({ face }: { face: Face }) {
  return (
    <>
      <span className="card-frame" aria-hidden="true" />
      <Index face={face} corner="tl" />
      <span className="card-pips" data-n={face.number} aria-hidden="true">
        {Array.from({ length: face.number }, (_, i) => (
          <Glyph key={i} color={face.color} size={20} />
        ))}
      </span>
      <Index face={face} corner="br" />
    </>
  );
}

/** The back of a card: a violet night in pixels, with a ringed world at its heart. */
export function CardBackArt() {
  return (
    <span className="card-back-art" aria-hidden="true">
      <svg
        className="card-back-emblem"
        viewBox={`0 0 ${ORB_CROWNED.w} ${ORB_CROWNED.h}`}
        shapeRendering="crispEdges"
      >
        <SpritePaths sprite={ORB_CROWNED} />
      </svg>
    </span>
  );
}

/**
 * A Stamp (docs/LONG_NIGHT_ART_DIRECTION.md §12.8), at the top center of the face, each a shape of
 * its own on a 20 × 20 grid: a brass rivet, a drop of wax, a coin with a hole, two rings, a
 * looping arrow, a glass bead (cracking before it shatters), a star, a smudge of chalk, a small
 * shield, and tally marks that add up.
 */
function StampMark({ stamp, value = 0 }: { stamp: StampId; value?: number }) {
  switch (stamp) {
    case 'brass-stamp':
      return (
        <>
          <circle cx="10" cy="10" r="6.5" className="s-brass" />
          <circle cx="8" cy="8" r="2" className="s-shine" />
        </>
      );
    case 'wax-seal':
      return (
        <path
          className="s-wax"
          d="M10 3c2 0 2.6 1.4 4 2s3 1.6 2.6 3.6 .4 3-1 4.4-2.6 3.6-5.6 3.4-3.8-1.6-5-3-1.6-3-1-4.6S6 4 7.4 3.6 9 3 10 3z"
        />
      );
    case 'coin-stamp':
      return (
        <>
          <circle cx="10" cy="10" r="7" className="s-brass" />
          <rect x="8.4" y="8.4" width="3.2" height="3.2" className="s-hole" />
        </>
      );
    case 'double-stamp':
      return (
        <>
          <circle cx="7.5" cy="10" r="4.6" className="s-ring" />
          <circle cx="12.5" cy="10" r="4.6" className="s-ring" />
        </>
      );
    case 'return-stamp':
      return <path className="s-line" d="M14.5 7.5A5.5 5.5 0 1 0 15 13M14.5 3.5v4h-4" />;
    case 'glass-stamp':
      return (
        <>
          <circle cx="10" cy="10" r="6" className="s-glass" />
          <circle cx="8" cy="7.8" r="1.6" className="s-shine" />
          {value > 0 && <path className="s-crack" d="M10 4.5l-1.2 3.4 2 1.6-1.6 3 1.2 3" />}
        </>
      );
    case 'lucky-stamp':
      return (
        <path
          className="s-gold"
          d="M10 2.5l2.2 4.6 5 .6-3.7 3.4 1 5-4.5-2.5-4.5 2.5 1-5L2.8 7.7l5-.6z"
        />
      );
    case 'chalk-stamp':
      return <ellipse cx="10" cy="10" rx="7" ry="4.5" className="s-chalk" />;
    case 'ward-stamp':
      return (
        <path className="s-ward" d="M10 2.5l6 2.3v4.6c0 4.2-2.8 6.6-6 7.6-3.2-1-6-3.4-6-7.6V4.8z" />
      );
    case 'tally-stamp': {
      const n = Math.max(1, Math.min(5, value || 1));
      return (
        <>
          {Array.from({ length: Math.min(4, n) }, (_, i) => (
            <path key={i} className="s-line" d={`M${5 + i * 3.4} 4.5v11`} />
          ))}
          {n >= 5 && <path className="s-line" d="M3 13L17 7" />}
        </>
      );
    }
  }
}

/** A Finish's glyph, in the bottom-left corner. */
const FINISH_GLYPH: Readonly<Record<FinishId, string>> = {
  foil: 'M3 15L15 3M7 15L15 7M3 11L11 3',
  gilt: 'M9 2c4 2 6 6 4 11-2 2-6 3-9 0C2 9 5 4 9 2z',
  pearl: 'M9 3a6 6 0 1 0 .01 0z',
  ghost: 'M4 16V8a5 5 0 0 1 10 0v8l-2-2-1.6 2-1.4-2-1.4 2L6 14z',
};

/** A Trick shows an object where the pips would be (§12.8), on a 40 × 40 grid. */
const TRICK_ART: Readonly<Record<TrickId, ReactNode>> = {
  reverse: <path d="M28 14a10 10 0 1 0 2 10M28 7v7h-7" />,
  gag: <path d="M8 18h24M8 22h24M12 18c0-4 16-4 16 0M14 26l-2 6M26 26l2 6" />,
  swap: <path d="M10 15h20l-5-5M30 25H10l5 5" />,
  shove: (
    <path d="M10 26V16c0-1.6 2.4-1.6 2.4 0v6M12.4 18v-5c0-1.6 2.4-1.6 2.4 0v8M14.8 14v-2c0-1.6 2.4-1.6 2.4 0v9M17.2 15c0-1.6 2.4-1.6 2.4 0v8l3-3c1.2-1.2 3 .4 2 1.6l-5 6c-2 2.4-4 3.4-7 3.4-3 0-3-2-3-4" />
  ),
  'chalk-line': <path d="M8 30L32 10M10 33l2-2M30 9l2-2" />,
  'draw-two': (
    <>
      <rect x="9" y="9" width="14" height="20" rx="2" />
      <rect x="17" y="12" width="14" height="20" rx="2" />
    </>
  ),
  hex: (
    <>
      <path d="M6 20c6-9 22-9 28 0-6 9-22 9-28 0z" />
      <circle cx="20" cy="20" r="4" />
    </>
  ),
  requiem: (
    <path d="M20 8c-6 0-8 6-8 12v4l-3 4h22l-3-4v-4c0-6-2-12-8-12zM17 30a3 3 0 0 0 6 0M20 5v3" />
  ),
  tithe: (
    <>
      <circle cx="20" cy="20" r="10" />
      <path d="M20 14v12M16.5 17c0-2 7-2 7 0s-7 2-7 4 7 2 7 0" />
    </>
  ),
};

/** A card: color is always paired with its glyph and number (§19.13). */
export function CardView({
  face,
  wild = false,
  epitaph,
  chalk = false,
  stamp,
  finish,
  trick,
  stampValue,
  tarnished = false,
  iou = false,
  back = false,
  size = 'md',
  state = 'neutral',
  label,
  hint,
  flipRoute = false,
  onClick,
  onFocus,
  onBlur,
  onPointerEnter,
  onPointerLeave,
  onPointerMove,
  children,
  pressed,
  disabled,
  id,
}: CardViewProps) {
  const color = face?.color;
  const classes = [
    'card',
    `card-${size}`,
    `card-${state}`,
    back ? 'card-back' : '',
    epitaph ? 'card-epitaph' : '',
    wild ? 'card-wild' : '',
    wild && face ? 'card-wild-named' : '',
    chalk ? 'card-chalk' : '',
    finish && !back ? `card-finish-${finish}` : '',
    trick && !back ? 'card-is-trick' : '',
    tarnished && !back ? 'card-tarnished' : '',
    iou && !back ? 'card-iou' : '',
    color && !back && !iou ? `c-${color}` : '',
  ]
    .filter(Boolean)
    .join(' ');
  const body = back ? (
    <CardBackArt />
  ) : iou ? (
    // An IOU: plain paper with no color, "IOU" in his ink and a blot of wax. It looks unplayable.
    <>
      <span className="card-iou-text" aria-hidden="true">
        IOU
      </span>
      <span className="card-iou-wax" aria-hidden="true" />
    </>
  ) : epitaph ? (
    <>
      <span className="card-frame" aria-hidden="true" />
      {epitaph.links.length > 0 ? (
        <span className="card-route" aria-hidden="true">
          <RouteGlyph links={epitaph.links} size={40} flip={flipRoute} />
        </span>
      ) : face ? (
        <span className="card-pips" data-n={1} aria-hidden="true">
          <Glyph color={face.color} size={20} />
        </span>
      ) : null}
      {face && <span className="card-epitaph-face">{`${face.color}${face.number}`}</span>}
      <span className="card-caption">{t('card.epitaph')}</span>
    </>
  ) : wild && !face ? (
    <>
      <span className="card-frame" aria-hidden="true" />
      <Index text="W" corner="tl" />
      <span className="card-pips card-pips-wild" aria-hidden="true">
        <WildEmblem size={30} />
      </span>
      <Index text="W" corner="br" />
    </>
  ) : face && trick ? (
    <>
      <span className="card-frame" aria-hidden="true" />
      <Index face={face} corner="tl" />
      <span className="card-pips card-trick-art" aria-hidden="true">
        <svg viewBox="0 0 40 40">{TRICK_ART[trick]}</svg>
      </span>
      <Index face={face} corner="br" />
    </>
  ) : face ? (
    <>
      <FaceArt face={face} />
      {wild && (
        <span className="card-wild-mark" aria-hidden="true">
          <PixelW />
        </span>
      )}
    </>
  ) : null;
  const content = (
    <>
      {body}
      {chalk && !back && (
        <span className="card-wild-mark card-chalk-mark" aria-hidden="true">
          ✎
        </span>
      )}
      {trick && !back && (
        <span className="card-trick" aria-hidden="true">
          {itemName(`trick.${trick}`)}
        </span>
      )}
      {stamp && !back && !iou && (
        <span className={`card-stamp stamp-${stamp}`} aria-hidden="true">
          <svg viewBox="0 0 20 20">
            <StampMark stamp={stamp} value={stampValue} />
          </svg>
        </span>
      )}
      {finish && !back && !iou && (
        <>
          <span className="card-finish-layer" aria-hidden="true" />
          <span className="card-finish-glyph" aria-hidden="true">
            <svg viewBox="0 0 18 18">
              <path d={FINISH_GLYPH[finish]} />
            </svg>
          </span>
        </>
      )}
      {children}
    </>
  );
  if (onClick)
    return (
      <button
        type="button"
        id={id}
        className={classes}
        aria-label={label}
        title={hint ?? label}
        aria-pressed={pressed}
        aria-disabled={disabled || undefined}
        onClick={onClick}
        onFocus={onFocus}
        onBlur={onBlur}
        onPointerEnter={onPointerEnter}
        onPointerLeave={onPointerLeave}
        onPointerMove={onPointerMove}
      >
        {content}
      </button>
    );
  return (
    <div
      id={id}
      className={classes}
      role={label ? 'img' : undefined}
      aria-label={label || undefined}
      aria-hidden={label ? undefined : true}
      title={hint ?? (label || undefined)}
    >
      {content}
    </div>
  );
}
